import type {
  TimeEntryItem,
  TimerStateItem,
  StartTimerResult,
  StopTimerResult,
  TimeEntrySummary,
  TeamTimeSummary,
  TimePeriodPresets,
  ProjectTimeStats,
  TaskTimeStats,
  TimeReportData,
  TimeReportConfig,
  PaginatedResponse,
  UserWritableTimeEntrySource,
  LoggableEmployee,
  OwnerProjectOption,
  ReopenedPeriodItem,
  ProjectTaskOption,
  ProjectListItem,
} from '@hygge/shared';
import { api } from './api';
import type { ProjectsQueryParams, ProjectsResponse } from './projects';
import { collectAllPages } from './all-pages';

// ─── Time Entries ──────────────────────────────────────────────────────────

export async function getTimeEntries(
  filters?: { from?: string; to?: string; projectId?: string; taskId?: string; page?: number; limit?: number },
): Promise<PaginatedResponse<TimeEntryItem>> {
  const { data } = await api.get<PaginatedResponse<TimeEntryItem>>('/time-entries', { params: filters });
  return data;
}

/**
 * Every entry in a window, not just the first page — the Weekly Timesheet sums
 * each day's and the week's total from the rows it holds, so a missing page is
 * a wrong total. See `collectAllPages`. [HW-869]
 */
export async function getAllTimeEntries(
  filters?: { from?: string; to?: string; projectId?: string; taskId?: string },
): Promise<PaginatedResponse<TimeEntryItem>> {
  return collectAllPages((page, limit) => getTimeEntries({ ...filters, page, limit }));
}

export async function createTimeEntry(payload: {
  projectId: string;
  /**
   * Whose hours these are — omitted for your own, which is every entry the
   * dialog sent before HW-864. Naming someone else needs authority over
   * `projectId`: managing it, or HR. [HW-862]
   */
  employeeId?: string;
  taskId?: string;
  description?: string;
  startedAt: string;
  endedAt?: string;
  duration: number;
  /**
   * The IANA zone whose wall clock `startedAt`'s digits were typed on — the
   * digits are stored as UTC, and this says whose clock they are. [HW-916]
   */
  timezone?: string;
  // Not `string`, and not the full TimeEntrySource: this is the hand-entry
  // endpoint, whose DTO rejects 'clockify' on purpose. Typing it loosely here
  // only lets a caller compile its way to a 400.
  source?: UserWritableTimeEntrySource;
}): Promise<TimeEntryItem> {
  const { data } = await api.post<TimeEntryItem>('/time-entries', payload);
  return data;
}

export async function updateTimeEntry(
  id: string,
  payload: Record<string, unknown>,
): Promise<TimeEntryItem> {
  const { data } = await api.patch<TimeEntryItem>(`/time-entries/${id}`, payload);
  return data;
}

export async function deleteTimeEntry(id: string): Promise<void> {
  await api.delete(`/time-entries/${id}`);
}

// ─── Summary ───────────────────────────────────────────────────────────────

export async function getTimeSummary(from: string, to: string): Promise<TimeEntrySummary> {
  const { data } = await api.get<TimeEntrySummary>('/time-entries/summary', { params: { from, to } });
  return data;
}

// ─── Team ──────────────────────────────────────────────────────────────────

export async function getTeamTimeEntries(
  filters?: { from?: string; to?: string; projectId?: string; employeeId?: string; page?: number; limit?: number },
): Promise<PaginatedResponse<TimeEntryItem>> {
  const { data } = await api.get<PaginatedResponse<TimeEntryItem>>('/time-entries/team', { params: filters });
  return data;
}

/**
 * Every team entry in a window across all its pages. Team Time sums each
 * person's and each project's total from the rows it holds, so a missing page
 * is a wrong total and can drop a person or a project from the list. See
 * `collectAllPages`. [HW-930]
 */
export async function getAllTeamTimeEntries(
  filters?: { from?: string; to?: string; projectId?: string; employeeId?: string },
): Promise<PaginatedResponse<TimeEntryItem>> {
  return collectAllPages((page, limit) => getTeamTimeEntries({ ...filters, page, limit }));
}

/**
 * Which days each period preset names today, on the company calendar — the
 * days a summarized report is counted over. Screens that fetch by explicit
 * days take their preset windows from here instead of the reader's clock.
 * [HW-929]
 */
export async function getTimePeriodPresets(): Promise<TimePeriodPresets> {
  const { data } = await api.get<TimePeriodPresets>('/time-entries/periods');
  return data;
}

/**
 * One period, split by project, by person and by day — the Dashboard tab's
 * whole payload. Guarded by `time.reports.team`, and it rejects a window it
 * cannot serve (unparseable dates, `from` after `to`, more than 400 days) with
 * a 400 whose message is written for the reader.
 */
export async function getTeamTimeSummary(params: {
  from: string;
  to: string;
  projectId?: string;
}): Promise<TeamTimeSummary> {
  const { data } = await api.get<TeamTimeSummary>('/time-entries/team-summary', { params });
  return data;
}

// ─── Project ───────────────────────────────────────────────────────────────

export async function getProjectTimeEntries(
  projectId: string,
  filters?: { from?: string; to?: string; page?: number; limit?: number },
): Promise<PaginatedResponse<TimeEntryItem>> {
  const { data } = await api.get<PaginatedResponse<TimeEntryItem>>(
    `/time-entries/project/${projectId}`,
    { params: filters },
  );
  return data;
}

export async function getProjectTimeStats(projectId: string): Promise<ProjectTimeStats> {
  const { data } = await api.get<ProjectTimeStats>(`/time-entries/project/${projectId}/stats`);
  return data;
}

// ─── Task ──────────────────────────────────────────────────────────────────

export async function getTaskTimeEntries(taskId: string): Promise<TaskTimeStats> {
  const { data } = await api.get<TaskTimeStats>(`/time-entries/task/${taskId}`);
  return data;
}

/**
 * Whose hours the caller may log on this project besides their own. An empty
 * list is the normal answer for someone without that authority — it is not an
 * error, and the dialog simply shows no picker. [HW-864]
 */
export async function getLoggableEmployees(projectId: string): Promise<LoggableEmployee[]> {
  const { data } = await api.get<LoggableEmployee[]>('/time-entries/loggable-employees', {
    params: { projectId },
  });
  return data;
}

/**
 * The open tasks of a project, for the Edit dialog's Task select. An empty
 * list is the ordinary answer for someone who may not log time there — not an
 * error. [HW-974]
 */
export async function getProjectTasks(projectId: string): Promise<ProjectTaskOption[]> {
  const { data } = await api.get<ProjectTaskOption[]>('/time-entries/project-tasks', {
    params: { projectId },
  });
  return data;
}

/**
 * The active projects an entry of `employeeId` may be moved to — that person's
 * own, since an entry stays on its owner's projects (2026-10-09 decision). An
 * empty list is the ordinary answer for a caller who may not edit that
 * person's entries — not an error. [HW-974]
 */
export async function getOwnerProjects(employeeId: string): Promise<OwnerProjectOption[]> {
  const { data } = await api.get<OwnerProjectOption[]>('/time-entries/owner-projects', {
    params: { employeeId },
  });
  return data;
}

/** The caller's own live reopened closed periods, for My Time's notice. [HW-963] */
export async function getMyReopenedPeriods(): Promise<ReopenedPeriodItem[]> {
  const { data } = await api.get<ReopenedPeriodItem[]>('/time-entries/reopened-periods');
  return data;
}

/**
 * A project as the report filters get it: what a filter shows and nothing
 * else. The route admits a reader on a report grant alone, so the Projects
 * list's description, boards and member roster stay behind `/projects`.
 * `managers` feeds the Report Builder's "Project manager" source. [HW-972]
 */
export type ReportProject = Pick<ProjectListItem, 'id' | 'name' | 'color' | 'status' | 'managers'>;

export interface ReportProjectsResponse {
  data: ReportProject[];
  meta: ProjectsResponse['meta'];
}

/**
 * The projects the report filters offer this reader: every project to one
 * whose reports read the whole company, otherwise the projects `/projects`
 * would list them. Same query and page as `/projects`, filter fields only.
 * [HW-972]
 */
export async function getReportProjects(params: ProjectsQueryParams): Promise<ReportProjectsResponse> {
  const { data } = await api.get<ReportProjectsResponse>('/time-entries/report-projects', { params });
  return data;
}

// ─── Timer ─────────────────────────────────────────────────────────────────

export async function getCurrentTimer(): Promise<TimerStateItem | null> {
  const { data } = await api.get<TimerStateItem | null>('/timer');
  return data;
}

export async function startTimer(payload: {
  taskId?: string;
  projectId?: string;
  /** The viewer's IANA zone. The server keeps the timer's start as a real
   *  instant; this zone is stored with the timer and used at stop, to write
   *  the entry on its author's clock. [HW-916] */
  timezone: string;
}): Promise<StartTimerResult> {
  const { data } = await api.post<StartTimerResult>('/timer/start', payload);
  return data;
}

/** `timezone` is the viewer's IANA zone at the moment of stopping. The server
 *  uses it only for a timer started without one — by a client older than
 *  HW-916 — so that entry is still written on its author's clock. [HW-916] */
export async function stopTimer(payload: { timezone: string }): Promise<StopTimerResult | null> {
  const { data } = await api.post<StopTimerResult | null>('/timer/stop', payload);
  return data;
}

// ─── Reports ──────────────────────────────────────────────────────────

export async function getReportData(params: {
  groupBy: 'project' | 'employee';
  from: string;
  to: string;
  projectId?: string;
  employeeId?: string;
}): Promise<TimeReportData> {
  const { data } = await api.get<TimeReportData>('/time-entries/report', { params });
  return data;
}

// ─── The report builder ────────────────────────────────────────────────────

/**
 * One aggregated group of a built report.
 *
 * Declared here rather than imported: the runner's result type lives in the
 * backend module (`report-builder.sql.ts`), which the frontend cannot reach,
 * and only the *configuration* is shared. Keep this in step with
 * `TimeReportRow` there — the two are read side by side in any change to the
 * result shape.
 */
export interface TimeReportResultRow {
  /** Machine keys, outer → inner. Empty string is the residual bucket. */
  keys: string[];
  /** What a reader sees, in the same order. Empty string means unlabelled. */
  label: string[];
  totalSeconds: number;
  /**
   * Whole percent of the report.
   *
   * The rows sum to exactly 100; a group total is rounded from its own hours,
   * so it can sit several points away from the sum of the rows beneath it —
   * as many as the group has rows. Equal hours print equal percentages, which
   * is what that buys.
   */
  sharePercent: number;
  dailyAverageSeconds: number;
  /**
   * Whether this row is a group's own total rather than one of its rows.
   *
   * Only a two-level report has any, and one stands above the rows it totals,
   * one key long where they are two. Its hours are counted a second time in
   * those rows, so anything that sums this list has to skip them — which is
   * what `rowsSumSeconds` does. [HW-877]
   */
  subtotal: boolean;
}

export interface TimeReportRunResult {
  config: TimeReportConfig;
  /**
   * The window the period actually resolved to, as calendar days.
   *
   * NOT the period that was asked for: with a week axis the server grows the
   * window to whole ISO weeks, so this is wider than the request. Everything
   * printed beside the total has to quote this.
   */
  window: { from: string; to: string };
  /**
   * The running month the "completed periods only" filter kept out, or null
   * when it kept nothing out.
   *
   * The server decides this: the screen holds a preset, not the dates it
   * resolved to, so working it out here would be a second reading of the
   * period the numbers were counted over. Keep in step with `TimeReportResult`
   * in the backend's `report-builder.sql.ts`.
   */
  openPeriodExcluded: { monthLabel: string } | null;
  totalSeconds: number;
  rows: TimeReportResultRow[];
  truncated: boolean;
}

export interface DraftedReportConfig {
  config: TimeReportConfig;
  /** What the description asked for that the server would not fill in. */
  unresolved: Array<{ field: string; text: string }>;
}

export type SavedReportScope = 'personal' | 'shared';

export interface SavedTimeReport {
  id: string;
  name: string;
  scope: SavedReportScope;
  /** Unparsed on purpose: a stored config is validated where it is loaded. */
  config: unknown;
  ownerId: string;
  owner?: { id: string; firstName: string; lastName: string };
  createdAt: string;
  updatedAt: string;
}

/**
 * Run a configuration. A POST because the body nests two levels deep and
 * carries id lists — and because running is an action the reader takes, not a
 * thing that happens as they type.
 */
export async function runTimeReport(config: TimeReportConfig): Promise<TimeReportRunResult> {
  const { data } = await api.post<TimeReportRunResult>('/time-entries/report/run', { config });
  return data;
}

/** A sentence in, a configuration out. Nothing is run and no number is returned. */
export async function draftReportConfig(description: string): Promise<DraftedReportConfig> {
  const { data } = await api.post<DraftedReportConfig>('/time-entries/report/draft', {
    description,
  });
  return data;
}

export async function getSavedReports(): Promise<SavedTimeReport[]> {
  const { data } = await api.get<SavedTimeReport[]>('/time-entries/saved-reports');
  return data;
}

export async function createSavedReport(body: {
  name: string;
  scope: SavedReportScope;
  config: TimeReportConfig;
}): Promise<SavedTimeReport> {
  const { data } = await api.post<SavedTimeReport>('/time-entries/saved-reports', body);
  return data;
}

export async function updateSavedReport(
  id: string,
  patch: { name?: string; scope?: SavedReportScope; config?: TimeReportConfig },
): Promise<SavedTimeReport> {
  const { data } = await api.patch<SavedTimeReport>(`/time-entries/saved-reports/${id}`, patch);
  return data;
}

export async function duplicateSavedReport(id: string): Promise<SavedTimeReport> {
  const { data } = await api.post<SavedTimeReport>(`/time-entries/saved-reports/${id}/duplicate`);
  return data;
}

export async function deleteSavedReport(id: string): Promise<void> {
  await api.delete(`/time-entries/saved-reports/${id}`);
}

// ─── Export ────────────────────────────────────────────────────────────────

export async function exportTimeEntries(
  filters?: { from?: string; to?: string; projectId?: string; employeeId?: string },
): Promise<Blob> {
  const { data } = await api.get('/time-entries/export', {
    params: filters,
    responseType: 'blob',
  });
  return data as Blob;
}
